import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";

/**
 * A panel that hangs off something on screen without moving it.
 *
 * ⚠️ **WHY THIS IS A HOOK AND NOT THREE COPIES, 2026-09-07.**
 *
 * Jonathan: *"i like the gate before allowing to click refresh. but it should
 * not move the whole page down. this is an issue in a few places. nothing
 * should ever make the page jump. if something needs to popup it should
 * overlay with a shadow border around."*
 *
 * The rule is the important half and it is absolute: **nothing in this app
 * expands in normal flow.** A control that swaps a 30px button for a 90px
 * confirm shoves everything below it down the page, and the reader's eye is
 * already travelling to where a number was. On a screen people commit payments
 * from, that is not a cosmetic complaint -- the figure you were reading is now
 * somewhere else, and the thing under your pointer is not what it was.
 *
 * `Menu` had already solved this and its own docstring says why: the panel is
 * `position: fixed`, so no ancestor's `overflow: hidden` can crop it, and its
 * placement is computed from the trigger's rect with a flip and a clamp. That
 * logic was correct and lived in exactly one component, so the other three
 * places that needed it grew in-flow expansions instead.
 *
 * All of it is here now, and `Menu` is one of the callers rather than the
 * owner. A second copy of this maths is a second thing to be wrong about the
 * sidebar, which is the ancestor that clips.
 *
 * WHAT IT HANDLES
 * ---------------
 *   * Placement from the anchor's own rect, flipped up when there is genuinely
 *     more room above and clamped to both viewport edges.
 *   * Outside click, Escape (focus returns to the anchor, so a keyboard user
 *     is not dropped at the top of the document), and reflow on scroll or
 *     resize -- captured, because a fixed panel does not travel with the thing
 *     it is anchored to and the coverage strip is `position: sticky`.
 *   * `visibility: hidden` until measured, so nothing renders at 0,0 for a
 *     frame.
 */

const GAP = 6;
const EDGE = 8;

/**
 * The left edge a fixed panel may not cross.
 *
 * ⚠️ WHY THIS EXISTS (Jonathan, 2026-11-04: the Dashboard's Consolidated /
 * Business / Personal info dot opened a note whose left half sat BEHIND the
 * navy rail). The sidebar is an in-flow flex child with its own `z-index`, so
 * it paints over a `position: fixed` panel that an earlier clamp had allowed to
 * slide left to the viewport's 8px edge — under the rail. A higher z-index would
 * only float a white note ON TOP of the navy nav, which is worse. So instead the
 * panel is clamped to the left edge of the content area (`#main`), i.e. the
 * sidebar's right edge on desktop. On a phone the rail is hidden and `#main`
 * spans the viewport, so this is just `EDGE` and nothing moves.
 */
function contentLeft(): number {
  if (typeof document === "undefined") return EDGE;
  const main = document.getElementById("main");
  if (!main) return EDGE;
  return Math.max(EDGE, Math.round(main.getBoundingClientRect().left));
}

interface Position {
  top: number;
  left: number;
  maxHeight: number;
}

export function useAnchored<T extends HTMLElement>(
  open: boolean,
  onClose: () => void,
  /**
   * Where the panel hangs.
   *
   *   "right" / "left"  -- below the anchor (or flipped above when there is no
   *                        room), with the panel's right or left edge on the
   *                        anchor's. The default for a menu dropped from a
   *                        control that sits in open space.
   *   "side"            -- beside the anchor, opening to its right into the
   *                        content area and clamped to the left only when the
   *                        right is genuinely out of room. For a trigger pinned
   *                        to the bottom-left corner of the sidebar, "above" is
   *                        the only way an above/below panel can go, and above
   *                        is exactly where the user's name line and the amber
   *                        "Update available" button live -- so the popover
   *                        landed on top of them (Jonathan, 2026-10-08: "it
   *                        covers the amber button and the words"). Opening
   *                        sideways puts it over the one region that is always
   *                        empty.
   */
  align: "left" | "right" | "side" = "right",
  /**
   * Hang off an element the caller already renders, instead of the button this
   * hook would otherwise own. `ConfirmPanel` needs it: its confirm opens from a
   * choice inside a `Menu`, so it anchors to the wrapper around that menu.
   *
   * ⚠️ Whatever is passed must NOT resize when the panel opens, or the anchor
   * moves the page and the whole point is lost.
   */
  external?: RefObject<HTMLElement | null>,
): {
  anchor: RefObject<T | null>;
  panel: RefObject<HTMLDivElement | null>;
  style: CSSProperties;
} {
  const own = useRef<T | null>(null);
  const anchor = (external ?? own) as RefObject<T | null>;
  const panel = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Position | null>(null);

  // `onClose` in a ref so the listeners below do not resubscribe on every
  // render of a caller that passes an inline arrow -- which all three of them
  // do.
  const close = useRef(onClose);
  close.current = onClose;

  const place = useCallback(() => {
    const host = anchor.current;
    const box = panel.current;
    if (!host || !box) return;

    const rect = host.getBoundingClientRect();
    const width = box.offsetWidth;
    const height = box.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Beside the anchor rather than above or below it. Prefer its right; fall
    // to its left only when the right cannot hold the panel. Bottom-aligned to
    // the anchor, so it grows up into the open content area from a trigger that
    // sits low in the sidebar, then clamped to both viewport edges.
    if (align === "side") {
      let left = rect.right + GAP;
      if (left + width > vw - EDGE) left = rect.left - GAP - width;
      left = Math.max(contentLeft(), Math.min(left, vw - width - EDGE));

      let top = rect.bottom - height;
      top = Math.max(EDGE, Math.min(top, vh - height - EDGE));

      setPos({ top, left, maxHeight: Math.max(120, vh - 2 * EDGE) });
      return;
    }

    const below = vh - rect.bottom - GAP - EDGE;
    const above = rect.top - GAP - EDGE;
    // Flip up only when there is genuinely more room up there. A panel that
    // flips on the first pixel of overflow jumps around as the page scrolls --
    // which would be this same defect in a new place.
    const flip = below < Math.min(height, 180) && above > below;

    const top = flip
      ? Math.max(EDGE, rect.top - GAP - height)
      : rect.bottom + GAP;

    let left = align === "right" ? rect.right - width : rect.left;
    left = Math.min(left, vw - width - EDGE);
    // Never under the in-flow sidebar: clamp to the content area's left edge,
    // not the viewport's, so a fixed panel the rail would paint over is pushed
    // clear of it (see `contentLeft`).
    left = Math.max(contentLeft(), left);

    setPos({ top, left, maxHeight: Math.max(120, flip ? above : below) });
  }, [align]);

  // Before paint, so the panel never shows at 0,0 for a frame first.
  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (anchor.current?.contains(target)) return;
      if (panel.current?.contains(target)) return;
      close.current();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      close.current();
      anchor.current?.focus?.();
    }
    // `true` so this fires for scrolls inside any container, not just the
    // window.
    function onReflow() {
      place();
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, place]);

  return {
    anchor,
    panel,
    style: pos
      ? { top: pos.top, left: pos.left, maxHeight: pos.maxHeight }
      : { visibility: "hidden" },
  };
}
